// ── Status & Enum Types ─────────────────────────────────────────────────────

export type BuildProjectStatus =
  | 'planning'
  | 'in_progress'
  | 'review'
  | 'completed'
  | 'on_hold'
  | 'cancelled';

export type BuildItemStatus =
  | 'planned'
  | 'in_progress'
  | 'review'
  | 'revision_needed'
  | 'completed'
  | 'on_hold'
  | 'cancelled';

export type TeamMemberRole = 'builder' | 'lead' | 'manager' | 'admin';

export type TeamMemberStatus = 'active' | 'inactive' | 'on_leave';

export type AssignmentRole = 'builder' | 'lead' | 'reviewer';

export type AssignmentStatus = 'active' | 'completed' | 'removed';

export type ProjectLinkType = 'related' | 'depends_on' | 'blocks';

export type ProjectCategory =
  | 'spawn'
  | 'hub'
  | 'arena'
  | 'lobby'
  | 'adventure_map'
  | 'decoration'
  | 'infrastructure';

// ── Core Entity Types ───────────────────────────────────────────────────────

export interface BuildTeamMember {
  id: string;
  minecraft_uuid: string;
  display_name: string;
  role: TeamMemberRole;
  status: TeamMemberStatus;
  specialties: string | null; // JSON string array
  joined_at: number;
  added_by: string;
  updated_at: number | null;
  updated_by: string | null;
  notes: string | null;
}

export interface BuildProject {
  id: string;
  title: string;
  description: string;
  status: BuildProjectStatus;
  priority: number;
  /** Optional Apogee catalog pack id when the project is linked to a modpack listing. */
  apogee_id?: number | null;
  /** Present on some API responses (e.g. project detail or embedded project). */
  images_count?: number;
  category: ProjectCategory | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  started_at: number | null;
  due_date: number | null;
  completed_at: number | null;
  metadata: string | null; // JSON string
}

export interface BuildItem {
  id: string;
  project_id: string | null;
  /** Optional Apogee pack id for the primary modpack this build targets. */
  apogee_id?: number | null;
  title: string;
  description: string;
  status: BuildItemStatus;
  priority: number;
  category: string | null;
  dimensions: string | null; // JSON string {"x","y","z"}
  coordinates: string | null; // JSON string {"world","x","y","z"}
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
  started_at: number | null;
  due_date: number | null;
  completed_at: number | null;
  metadata: string | null; // JSON string
}

export interface BuildAssignment {
  id: string;
  member_id: string;
  target_id: string;
  target_type: 'project' | 'build';
  role: AssignmentRole;
  assigned_at: number;
  assigned_by: string;
  unassigned_at: number | null;
  unassigned_by: string | null;
  status: AssignmentStatus;
  target_title?: string | null;
  target_status?: string | null;
  project_id?: string | null;
  project_title?: string | null;
  schematic_count?: number;
}

export type BuildSchematicFileKind = 'schematic' | 'screenshot';

export interface BuildSchematic {
  id: string;
  r2_key: string;
  /** Name the uploader gave the file. Null on files uploaded before titles existed; show the filename then. */
  title: string | null;
  filename: string;
  content_type: string;
  /** Distinguishes schematic files from screenshots in the same bucket. */
  file_kind?: BuildSchematicFileKind;
  size: number;
  version: string | null;
  description: string | null;
  uploader_id: string;
  created_at: number;
  updated_at: number | null;
  updated_by: string | null;
  metadata: string | null;
  linked_targets?: { target_id: string; target_type: 'project' | 'build'; title: string | null }[];
}

export interface BuildSchematicLink {
  schematic_id: string;
  target_id: string;
  target_type: 'project' | 'build';
  linked_by: string;
  linked_at: number;
}

export interface BuildProjectMedia {
  id: string;
  project_id: string | null;
  r2_key: string;
  filename: string;
  content_type: string;
  size: number;
  caption: string | null;
  source_comment_id: string | null;
  created_at: number;
}

export interface BuildComment {
  id: string;
  target_id: string;
  target_type: 'project' | 'build';
  content: string;
  author_id: string;
  created_at: number;
  updated_at: number | null;
  updated_by: string | null;
  /** Gallery images attached to this comment (project gallery when project_id is set server-side). */
  images?: BuildProjectMedia[];
}

export interface BuildTag {
  id: string;
  name: string;
  color: string; // hex #RRGGBB
  description: string | null;
  created_at: number;
  created_by: string;
}

export interface BuildServerLink {
  id: string;
  target_id: string;
  target_type: 'project' | 'build';
  fqdn: string | null;
  cluster_id: string | null;
  server_name: string | null;
  notes: string | null;
  linked_by: string;
  linked_at: number;
  updated_at: number | null;
  updated_by: string | null;
}

export interface BuildModpackLink {
  id: string;
  target_id: string;
  target_type: 'project' | 'build';
  modpack_name: string;
  /** Present when linked via Apogee catalog */
  apogee_id?: number | null;
  modpack_version: string | null;
  notes: string | null;
  linked_by: string;
  linked_at: number;
  updated_at: number | null;
  updated_by: string | null;
}

export interface BuildCalendarLink {
  target_id: string;
  target_type: 'project' | 'build';
  event_id: string;
  linked_by: string;
  linked_at: number;
}

export interface BuildActivityLogEntry {
  id: string;
  target_id: string;
  target_type: 'build' | 'project' | 'team_member';
  action: string;
  description: string;
  old_value: string | null;
  new_value: string | null;
  actor_id: string;
  created_at: number;
}

export interface BuildProjectLink {
  source_project_id: string;
  target_project_id: string;
  link_type: ProjectLinkType;
  linked_by: string;
  linked_at: number;
}

export interface BuildTicketLink {
  ticket_id: number;
  target_id: string;
  target_type: 'build' | 'build_project';
  linked_by: string;
  linked_at: number;
}

// ── Full / Relation Types ───────────────────────────────────────────────────

export interface BuildProjectFull extends BuildProject {
  builds: BuildItem[];
  assignments: BuildAssignment[];
  tags: BuildTag[];
  schematics_count: number;
  comments_count: number;
  server_links: BuildServerLink[];
  modpack_links: BuildModpackLink[];
  latest_activity?: BuildActivityLogEntry[];
}

export interface BuildItemFull extends BuildItem {
  project: BuildProject | null;
  assignments: BuildAssignment[];
  tags: BuildTag[];
  schematics_count: number;
  comments_count: number;
  server_links: BuildServerLink[];
  modpack_links: BuildModpackLink[];
  latest_activity?: BuildActivityLogEntry[];
}

// ── Stats Type ──────────────────────────────────────────────────────────────

export interface BuildDashboardStats {
  total_projects: number;
  total_builds: number;
  total_team_members: number;
  projects_by_status: Record<string, number>;
  builds_by_status: Record<string, number>;
  projects_by_priority: Record<string, number>;
  builds_by_priority: Record<string, number>;
  recently_active: number;
  total_schematics: number;
  /** Rows in build_project_media (gallery + comment images tied to projects). */
  total_project_images?: number;
  total_active_assignments: number;
}

export interface BuildSchematicBulkUploadResult {
  created: BuildSchematic[];
  failed: { filename: string; error: string }[];
}

// ── Paginated Response ──────────────────────────────────────────────────────

export interface BuildPaginatedResponse<T> {
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  results: T[];
}

// ── Request Types ───────────────────────────────────────────────────────────

export interface CreateProjectRequest {
  title: string;
  description: string;
  status?: BuildProjectStatus;
  priority?: number;
  category?: ProjectCategory;
  apogee_id?: number | null;
  due_date?: number | null;
  started_at?: number | null;
  metadata?: string;
  tag_ids?: string[];
}

export interface UpdateProjectRequest {
  title?: string;
  description?: string;
  status?: BuildProjectStatus;
  priority?: number;
  category?: ProjectCategory | null;
  apogee_id?: number | null;
  due_date?: number | null;
  started_at?: number | null;
  completed_at?: number | null;
  metadata?: string | null;
}

export interface CreateBuildItemRequest {
  project_id?: string | null;
  apogee_id?: number | null;
  title: string;
  description: string;
  status?: BuildItemStatus;
  priority?: number;
  category?: string;
  dimensions?: string;
  coordinates?: string;
  due_date?: number | null;
  started_at?: number | null;
  metadata?: string;
  tag_ids?: string[];
}

export interface UpdateBuildItemRequest {
  title?: string;
  description?: string;
  status?: BuildItemStatus;
  priority?: number;
  category?: string | null;
  project_id?: string | null;
  apogee_id?: number | null;
  dimensions?: string | null;
  coordinates?: string | null;
  due_date?: number | null;
  started_at?: number | null;
  completed_at?: number | null;
  metadata?: string | null;
}

export interface CreateTeamMemberRequest {
  minecraft_uuid: string;
  display_name: string;
  role?: TeamMemberRole;
  status?: TeamMemberStatus;
  specialties?: string;
  notes?: string;
}

export interface UpdateTeamMemberRequest {
  display_name?: string;
  role?: TeamMemberRole;
  status?: TeamMemberStatus;
  specialties?: string | null;
  notes?: string | null;
}

export interface CreateAssignmentRequest {
  member_id: string;
  role?: AssignmentRole;
}

export interface CreateBuildServerLinkRequest {
  fqdn?: string;
  cluster_id?: string;
  server_name?: string;
  notes?: string;
}

export interface UpdateBuildServerLinkRequest {
  fqdn?: string | null;
  cluster_id?: string | null;
  server_name?: string | null;
  notes?: string | null;
}

export interface CreateBuildModpackLinkRequest {
  modpack_name: string;
  apogee_id?: number;
  modpack_version?: string;
  notes?: string;
}

export interface UpdateBuildModpackLinkRequest {
  modpack_name?: string;
  apogee_id?: number | null;
  modpack_version?: string | null;
  notes?: string | null;
}

export interface CreateBuildTagRequest {
  name: string;
  color: string;
  description?: string;
}

export interface UpdateBuildTagRequest {
  name?: string;
  color?: string;
  description?: string | null;
}

// ── Filter / Query Types ────────────────────────────────────────────────────

export interface ProjectListParams {
  status?: BuildProjectStatus;
  priority_min?: number;
  priority_max?: number;
  category?: ProjectCategory;
  assignee?: string;
  tag_id?: string;
  search?: string;
  sort_by?: 'created_at' | 'priority' | 'status' | 'due_date' | 'updated_at' | 'title';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface BuildItemListParams {
  project_id?: string;
  status?: BuildItemStatus;
  priority_min?: number;
  priority_max?: number;
  category?: string;
  assignee?: string;
  tag_id?: string;
  search?: string;
  sort_by?: 'created_at' | 'priority' | 'status' | 'due_date' | 'updated_at' | 'title';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// ── Constants ───────────────────────────────────────────────────────────────

export const BUILD_PROJECT_STATUSES: BuildProjectStatus[] = [
  'planning',
  'in_progress',
  'review',
  'completed',
  'on_hold',
  'cancelled',
];

export const BUILD_ITEM_STATUSES: BuildItemStatus[] = [
  'planned',
  'in_progress',
  'review',
  'revision_needed',
  'completed',
  'on_hold',
  'cancelled',
];

export const TEAM_MEMBER_ROLES: TeamMemberRole[] = [
  'builder',
  'lead',
  'manager',
  'admin',
];

export const TEAM_MEMBER_STATUSES: TeamMemberStatus[] = [
  'active',
  'inactive',
  'on_leave',
];

export const ASSIGNMENT_ROLES: AssignmentRole[] = [
  'builder',
  'lead',
  'reviewer',
];

export const PROJECT_CATEGORIES: ProjectCategory[] = [
  'spawn',
  'hub',
  'arena',
  'lobby',
  'adventure_map',
  'decoration',
  'infrastructure',
];

export const PROJECT_LINK_TYPES: ProjectLinkType[] = [
  'related',
  'depends_on',
  'blocks',
];
